export const CORRELATION_HEADERS = {
  sessionId: "X-MM-Session-Id",
  eventId: "X-MM-Event-Id",
} as const;

export const CORRELATION_ATTRIBUTES = {
  sessionId: "mm.session.id",
  eventId: "mm.event.id",
} as const;

export const OBSERVABILITY_EDGE_PATHS = {
  sentry: "/m/v1/e",
  postHog: "/m/v1/i",
} as const;

// This is deliberately fail-closed: enabling a PostHog SDK feature that calls
// a new endpoint requires an explicit review before the first-party proxy will forward it.
export const POSTHOG_PROXY_PATH_PREFIXES = [
  "/e",
  "/i",
  "/batch",
  "/capture",
  "/decide",
  "/flags",
  "/config",
  "/s",
  "/engage",
  "/api/early_access_features",
  "/api/product_tours",
  "/api/surveys",
  "/api/web_experiments",
  "/static",
] as const;

const SAFE_POSTHOG_PROJECT_KEY = /^[A-Za-z0-9_-]{1,200}$/;

export const createPostHogProxyPath = (projectKey: string): string => {
  const normalized = projectKey.trim();

  if (!SAFE_POSTHOG_PROJECT_KEY.test(normalized)) {
    throw new Error("A valid PostHog project key is required for the observability proxy");
  }

  return `${OBSERVABILITY_EDGE_PATHS.postHog}/${encodeURIComponent(normalized)}`;
};

export interface ObservabilityMetadata {
  serviceVersion?: string;
  deploymentEnvironment?: string;
}

const MAX_SESSION_ID_LENGTH = 128;
const SAFE_SESSION_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]*$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const sanitizeSessionId = (value: string | undefined): string | undefined => {
  const normalized = value?.trim();

  if (!normalized || normalized.length > MAX_SESSION_ID_LENGTH || !SAFE_SESSION_ID.test(normalized)) {
    return undefined;
  }

  return normalized;
};

export const sanitizeEventId = (value: string | undefined): string | undefined => {
  const normalized = value?.trim();

  if (!normalized || !UUID.test(normalized)) {
    return undefined;
  }

  return normalized.toLowerCase();
};
